import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Users, Trophy, AlertCircle, PieChart } from 'lucide-react';

type CalculatorInputs = {
  prizeValue: string;
  expectedParticipants: string;
  platformFee: number;
  processingFee: number;
};

const RaffleProfitCalculator = () => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    prizeValue: '',
    expectedParticipants: '',
    platformFee: 10, // 10% platform fee
    processingFee: 2.9, // 2.9% + $0.30 for payment processing
  });

  const [results, setResults] = useState<{
    recommendedPrice: number;
    breakEvenTickets: number;
    potentialProfit: number;
    feeBreakdown: {
      platform: number;
      processing: number;
      prizes: number;
      profit: number;
    };
  } | null>(null);

  const calculateResults = () => {
    const prizeValue = parseFloat(inputs.prizeValue) || 0;
    const expectedParticipants = parseInt(inputs.expectedParticipants) || 0;

    if (prizeValue <= 0 || expectedParticipants <= 0) {
      return null;
    }

    // Calculate minimum ticket price needed to break even
    const totalCosts = prizeValue;
    const platformFeeMultiplier = inputs.platformFee / 100;
    const processingFeeMultiplier = inputs.processingFee / 100;

    // Calculate recommended price with 20% margin for safety
    const basePrice = totalCosts / expectedParticipants;
    const recommendedPrice = basePrice / (1 - (platformFeeMultiplier + processingFeeMultiplier)) * 1.2;

    // Round to nearest $0.50
    const roundedPrice = Math.ceil(recommendedPrice * 2) / 2;

    // Calculate break-even number of tickets
    const breakEvenTickets = Math.ceil(
      totalCosts / (roundedPrice * (1 - (platformFeeMultiplier + processingFeeMultiplier)))
    );

    // Calculate potential profit
    const totalRevenue = roundedPrice * expectedParticipants;
    const platformFees = totalRevenue * platformFeeMultiplier;
    const processingFees = totalRevenue * processingFeeMultiplier;
    const potentialProfit = totalRevenue - platformFees - processingFees - prizeValue;

    setResults({
      recommendedPrice: roundedPrice,
      breakEvenTickets,
      potentialProfit,
      feeBreakdown: {
        platform: platformFees,
        processing: processingFees,
        prizes: prizeValue,
        profit: potentialProfit,
      },
    });
  };

  useEffect(() => {
    calculateResults();
  }, [inputs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="bg-purple-600 p-6 text-white">
        <div className="flex items-center justify-center mb-4">
          <Calculator className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-center">Raffle Profit Calculator</h2>
        <p className="text-center text-white/80 mt-2">
          Calculate the optimal ticket price for your raffle
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Trophy className="h-4 w-4 inline-block mr-2" />
              Total Prize Value ($)
            </label>
            <input
              type="number"
              name="prizeValue"
              value={inputs.prizeValue}
              onChange={handleInputChange}
              placeholder="e.g., 1000"
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="h-4 w-4 inline-block mr-2" />
              Expected Participants
            </label>
            <input
              type="number"
              name="expectedParticipants"
              value={inputs.expectedParticipants}
              onChange={handleInputChange}
              placeholder="e.g., 200"
              className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              min="0"
            />
          </div>
        </div>

        {results && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-purple-800 mb-2">
                  Recommended Ticket Price
                </h3>
                <p className="text-2xl font-bold text-purple-900">
                  ${results.recommendedPrice.toFixed(2)}
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-800 mb-2">
                  Break-even Tickets
                </h3>
                <p className="text-2xl font-bold text-blue-900">
                  {results.breakEvenTickets}
                </p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-800 mb-2">
                  Potential Profit
                </h3>
                <p className="text-2xl font-bold text-green-900">
                  ${Math.max(0, results.potentialProfit).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <PieChart className="h-4 w-4 mr-2" />
                Fee Breakdown (per ticket)
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Platform Fee ({inputs.platformFee}%)</span>
                  <span className="font-medium text-gray-900">
                    ${(results.feeBreakdown.platform / parseInt(inputs.expectedParticipants)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Processing Fee ({inputs.processingFee}%)</span>
                  <span className="font-medium text-gray-900">
                    ${(results.feeBreakdown.processing / parseInt(inputs.expectedParticipants)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Prize Contribution</span>
                  <span className="font-medium text-gray-900">
                    ${(results.feeBreakdown.prizes / parseInt(inputs.expectedParticipants)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-gray-600">Profit per Ticket</span>
                  <span className="font-medium text-gray-900">
                    ${(results.feeBreakdown.profit / parseInt(inputs.expectedParticipants)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Tips for Success</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>Consider offering early bird discounts to drive initial sales</li>
                      <li>Market your raffle through multiple channels to reach your participant goal</li>
                      <li>Set a realistic timeline that allows for proper promotion</li>
                      <li>Be transparent about prize values and drawing dates</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RaffleProfitCalculator;